<template>
<div>
    <div class="mb-3">
        <b-button v-if="canAdd" @click="toggleAdd" :variant="addNew ? 'danger' : 'primary'">
            <i :class="addNew ? 'fa-solid fa-ban' : 'fa-solid fa-plus'"></i> {{ addNew ? 'Cancel' : 'Add echo' }}
        </b-button>
        <b-collapse v-model="addNew" class="mt-3">
            <b-card>
                <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(onSubmit)" novalidate>

                    <h6 class="text-muted mb-3">Scan</h6>
                    <b-row>
                        <b-col md="3">
                            <validation-provider vid="date" v-slot="{ errors }">
                                <b-form-group label="Date" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-datepicker v-model="form.date" :locale="currentLang" :max="today" reset-button placeholder="Date..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="type" v-slot="{ errors }">
                                <b-form-group label="Type" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.type">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in typeOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="2">
                            <validation-provider vid="weeks" v-slot="{ errors }">
                                <b-form-group label="Weeks" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" max="45" v-model.number="form.weeks" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="2">
                            <validation-provider vid="days" v-slot="{ errors }">
                                <b-form-group label="Days" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" max="6" v-model.number="form.days" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="2">
                            <validation-provider vid="fetus_count" v-slot="{ errors }">
                                <b-form-group label="Fetuses" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="1" v-model.number="form.fetus_count" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>

                    <h6 class="text-muted mt-2 mb-3">Biometry (mm)</h6>
                    <b-row>
                        <b-col md="3">
                            <validation-provider vid="bpd_mm" v-slot="{ errors }">
                                <b-form-group label="BPD" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.1" min="0" v-model.number="form.bpd_mm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="hc_mm" v-slot="{ errors }">
                                <b-form-group label="HC" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.1" min="0" v-model.number="form.hc_mm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="ac_mm" v-slot="{ errors }">
                                <b-form-group label="AC" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.1" min="0" v-model.number="form.ac_mm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="fl_mm" v-slot="{ errors }">
                                <b-form-group label="FL" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.1" min="0" v-model.number="form.fl_mm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>

                    <h6 class="text-muted mt-2 mb-3">Findings & measurements</h6>
                    <b-row>
                        <b-col md="3">
                            <validation-provider vid="efw_grams" v-slot="{ errors }">
                                <b-form-group label="EFW (g)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" v-model.number="form.efw_grams" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="afi_cm" v-slot="{ errors }">
                                <b-form-group label="AFI (cm)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.1" min="0" v-model.number="form.afi_cm" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="presentation" v-slot="{ errors }">
                                <b-form-group label="Presentation" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.presentation">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in presentationOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="doppler" v-slot="{ errors }">
                                <b-form-group label="Doppler" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.doppler">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in dopplerOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="3">
                            <validation-provider vid="placenta_location" v-slot="{ errors }">
                                <b-form-group label="Placenta location" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.placenta_location">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in placentaLocationOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="placenta_grade" v-slot="{ errors }">
                                <b-form-group label="Placenta grade" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.placenta_grade">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option v-for="o in placentaGradeOptions" :key="o.value" :value="o.value">{{ o.text }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider vid="performed_by" v-slot="{ errors }">
                                <b-form-group label="Performed by" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.performed_by" placeholder="Sonographer / performer..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <validation-provider vid="findings" v-slot="{ errors }">
                        <b-form-group label="Findings" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-textarea rows="2" max-rows="6" v-model="form.findings" placeholder="Findings..." />
                        </b-form-group>
                    </validation-provider>

                    <b-button class="mr-1" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> {{ editingId ? 'Save' : 'Submit' }}</b-button>
                    <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="resetForm"><i class="fa-solid fa-ban"></i> Reset</b-button>
                </b-form>
                </validation-observer>
            </b-card>
        </b-collapse>
    </div>

    <b-overlay :show="confirm" no-wrap @shown="onShown">
        <template #overlay>
            <div ref="dialog" tabindex="-1" role="dialog" aria-modal="false">
                <p class="text-center">
                    <i class="fa-light fa-xmark text-danger"></i> <strong class="text-danger">DELETE echo?</strong>
                </p>
                <div class="text-center">
                    <b-button variant="outline-danger" class="mr-3" @click="onCancel">Cancel</b-button>
                    <b-button variant="outline-success" @click="onConfirmed(selected)">Confirm</b-button>
                </div>
            </div>
        </template>
    </b-overlay>

    <b-table
        responsive
        stacked="sm"
        show-empty
        striped
        hover
        small
        primary-key="id"
        :current-page="currentPage"
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortedBy"
        :sort-desc.sync="sortedDesc">

        <template v-slot:cell(date)="data">
            <span :class="{ 'small font-italic text-muted': !data.value }">{{ data.value ? toLocaleDate(data.value) : 'n/a' }}</span>
        </template>
        <template v-slot:cell(type)="data">
            <span class="text-capitalize">{{ typeLabel(data.value) }}</span>
        </template>
        <template v-slot:cell(ga)="row">
            <span>{{ gaDisplay(row.item) }}</span>
        </template>
        <template v-slot:cell(efw_grams)="data">
            <span :class="{ 'small font-italic text-muted': !data.value }">{{ data.value ? `${data.value} g` : 'n/a' }}</span>
        </template>
        <template v-slot:cell(presentation)="data">
            <span class="text-capitalize" :class="{ 'small font-italic text-muted': !data.value }">{{ data.value || 'n/a' }}</span>
        </template>
        <template v-slot:cell(actions)="row">
            <b-button v-if="canChange" size="sm" variant="outline-primary" class="mr-1" @click="onEdit(row.item)"><i class="fa-light fa-pen"></i></b-button>
            <b-button v-if="canDelete" size="sm" variant="danger" @click="onRemove(row.item)"><i class="fa-light fa-xmark"></i></b-button>
        </template>
        <template v-slot:empty>
            <div class="small text-muted text-center my-2"><span>No echos recorded</span></div>
        </template>
    </b-table>
    <b-pagination v-if="totalRows > perPage" class="m-0" align="right" :total-rows="totalRows" v-model="currentPage" :per-page="perPage" />
</div>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapActions } from "vuex"

export default FormExtension.extend({
    name: "early-ultrasound-list",
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            today: new Date(),
            addNew: false,
            editingId: null,
            confirm: false,
            selected: null,
            currentPage: 1,
            perPage: 5,
            sortedBy: 'date',
            sortedDesc: true,
            typeOptions: [
                { value: 'dating', text: 'Dating' },
                { value: 'nt', text: 'NT 11-14wk' },
                { value: 'anomaly', text: 'Anomaly 18-22wk' },
                { value: 'growth', text: 'Growth' },
                { value: 'other', text: 'Other' },
            ],
            presentationOptions: [
                { value: 'cephalic', text: 'Cephalic' },
                { value: 'breech', text: 'Breech' },
                { value: 'transverse', text: 'Transverse' },
                { value: 'oblique', text: 'Oblique' },
                { value: 'variable', text: 'Variable' },
            ],
            dopplerOptions: [
                { value: 'normal', text: 'Normal' },
                { value: 'abnormal', text: 'Abnormal' },
                { value: 'not_done', text: 'Not done' },
            ],
            placentaLocationOptions: [
                { value: 'anterior', text: 'Anterior' },
                { value: 'posterior', text: 'Posterior' },
                { value: 'fundal', text: 'Fundal' },
                { value: 'left_lateral', text: 'Left lateral' },
                { value: 'right_lateral', text: 'Right lateral' },
                { value: 'low_lying', text: 'Low-lying' },
                { value: 'praevia', text: 'Praevia' },
            ],
            placentaGradeOptions: [
                { value: '0', text: 'Grade 0' },
                { value: 'I', text: 'Grade I' },
                { value: 'II', text: 'Grade II' },
                { value: 'III', text: 'Grade III' },
            ],
            form: new Form({
                date: null,
                type: null,
                weeks: null,
                days: null,
                bpd_mm: null,
                hc_mm: null,
                ac_mm: null,
                fl_mm: null,
                efw_grams: null,
                afi_cm: null,
                placenta_location: null,
                placenta_grade: null,
                presentation: null,
                fetus_count: null,
                doppler: null,
                performed_by: "",
                findings: "",
                consistency_token: null,
            }),
            fields: [
                { key: 'date', label: 'Date', class: 'align-middle' },
                { key: 'type', label: 'Type', class: 'align-middle' },
                { key: 'ga', label: 'GA', class: 'align-middle' },
                { key: 'efw_grams', label: 'EFW', class: 'align-middle' },
                { key: 'presentation', label: 'Presentation', class: 'align-middle' },
                { key: 'findings', label: 'Findings', class: 'align-middle' },
                { key: 'actions', label: '', class: 'align-middle text-right' },
            ],
        }
    },
    computed: {
        items() {
            return this.record?.ultrasounds || []
        },
        totalRows() {
            return this.items.length
        },
        canAdd() {
            return this.checkUserPermission('maternity_ultrasound', 'can_add')
        },
        canChange() {
            return this.checkUserPermission('maternity_ultrasound', 'can_change')
        },
        canDelete() {
            return this.checkUserPermission('maternity_ultrasound', 'can_delete')
        },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.isDirty = this.form.dirty()
                if (this.isDirty) {
                    this.dirty()
                } else {
                    this.validated()
                }
            },
        },
    },
    methods: {
        ...mapActions('maternity', ['createUltrasound', 'updateUltrasound', 'deleteUltrasound']),
        typeLabel(value) {
            const o = this.typeOptions.find(opt => opt.value === value)
            return o ? o.text : (value || 'n/a')
        },
        gaDisplay(item) {
            if (item.weeks === null && item.days === null) {
                return 'n/a'
            }
            return `${item.weeks || 0}w+${item.days || 0}d`
        },
        toggleAdd() {
            this.addNew = !this.addNew
            if (!this.addNew) {
                this.resetForm()
            }
        },
        onEdit(item) {
            this.editingId = item.id
            this.form.withData(item)
            this.addNew = true
        },
        onSubmit() {
            const payload = { ...this.form.data(), record: this.record.id }
            const action = this.editingId
                ? this.updateUltrasound({ ...payload, id: this.editingId })
                : this.createUltrasound(payload)

            action
                .then(() => {
                    this.notify("Saved", "Echo saved successfully")
                    this.resetForm()
                    this.addNew = false
                })
                .catch(errors => {
                    if (errors.response) {
                        this.$refs.observer?.setErrors(errors.response.data)
                        this.hasErrors = true
                    }
                })
        },
        resetForm() {
            this.editingId = null
            this.form.reset()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()
            })
        },
        onRemove(item) {
            this.confirm = true
            this.selected = item
        },
        onConfirmed(item) {
            this.deleteUltrasound(item.id)
                .then(() => {
                    this.notify("Removed", "Echo removed successfully")
                    this.confirm = false
                    this.selected = null
                })
                .catch(() => {
                    this.notify("Error", "Something went wrong", 'danger')
                    this.confirm = false
                    this.selected = null
                })
        },
        onShown() {
            this.$refs.dialog?.focus()
        },
        onCancel() {
            this.confirm = false
            this.selected = null
        },
    },
})
</script>
